import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import Axios from '../../../networking/intraceptor';
import * as endPoints from '../../../networking/urlEndPoints';
import { safeToast } from '../../../utils/helper/helper';
import {
  DELIVERED_RX_REASON_SNOOZE_OPTIONS,
  getAppTimeLocalValue,
  getDeliveredRxReasonSnoozeUntilDate,
} from './DeliveredRxReasonChangeReviewShared';

const SNOOZE_STORAGE_PREFIX = 'unsentScheduleSendPromptSnooze';
const POLL_MS = 5 * 60 * 1000;

const getSnoozeStorageKey = (pharmacyId, userId) => (
  `${SNOOZE_STORAGE_PREFIX}:${pharmacyId || 'pharmacy'}:${userId || 'user'}`
);

const getStoredSnoozeUntilMs = (storageKey) => {
  try {
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) return null;

    const parsedValue = JSON.parse(storedValue);
    const snoozeUntilMs = new Date(parsedValue?.snoozeUntil || parsedValue).getTime();
    if (Number.isNaN(snoozeUntilMs) || snoozeUntilMs <= Date.now()) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return snoozeUntilMs;
  } catch (_) {
    localStorage.removeItem(storageKey);
    return null;
  }
};

const UnsentScheduleSendPromptModal = ({
  records,
  totalCount,
  submitting,
  onSubmit,
  onSkip,
}) => {
  const [snoozeOption, setSnoozeOption] = useState(DELIVERED_RX_REASON_SNOOZE_OPTIONS[0].value);
  const [customSnoozeTime, setCustomSnoozeTime] = useState(() => (
    getAppTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
  ));
  const [isSkipMenuOpen, setIsSkipMenuOpen] = useState(false);

  if (!records.length) return null;

  const isCustomSnooze = snoozeOption === 'custom';
  const isSkipDisabled = submitting || (isCustomSnooze && !customSnoozeTime);

  const handleSkipClick = (snoozeConfig) => {
    onSkip({
      minutes: snoozeConfig?.minutes,
      customTime: snoozeConfig?.customTime,
    });
    setIsSkipMenuOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[170] flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-visible rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-100 to-gray-100 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
              <ReportProblemOutlinedIcon fontSize="small" />
            </span>
            <h2 className="text-lg font-semibold text-gray-900">Schedules Not Sent to WinRx</h2>
            {totalCount > 1 && (
              <span className="rounded-md bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
                {totalCount} pending
              </span>
            )}
          </div>
        </div>

        <div className="max-h-[58vh] overflow-auto bg-white p-4">
          <table className="w-full border-collapse border border-gray-200 text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[50px] border border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">#</th>
                <th className="w-[120px] border border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Rx</th>
                <th className="w-[220px] border border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Patient</th>
                <th className="border border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Drug</th>
                <th className="w-[100px] border border-gray-200 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-700">Qty</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {records.map((row, index) => (
                <tr key={row?.id || row?.rxnumber || index} className="align-top hover:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2.5 text-xs text-gray-500 font-bold">{index + 1}</td>
                  <td className="border border-gray-200 px-3 py-2.5 font-semibold text-gray-900">{row?.rxnumber ? `RX ${row.rxnumber}` : '-'}</td>
                  <td className="border border-gray-200 px-3 py-2.5 font-medium text-gray-900">{row?.patient_name || '-'}</td>
                  <td className="border border-gray-200 px-3 py-2.5 text-gray-900">{row?.drug || '-'}</td>
                  <td className="border border-gray-200 px-3 py-2.5 text-center font-bold text-gray-900">{row?.quantity != null ? row.quantity : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="relative z-30 flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsSkipMenuOpen((isOpen) => !isOpen)}
              disabled={submitting}
              className={`relative z-10 border px-5 py-2 text-sm font-semibold transition ${
                submitting
                  ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              } ${isSkipMenuOpen ? 'rounded-b-md rounded-t-none border-t-white' : 'rounded-md'}`}
            >
              Skip / Not Now
            </button>
            {isSkipMenuOpen && (
              <div className="absolute bottom-full right-0 z-50 -mb-px w-52 overflow-hidden rounded-md rounded-br-none border border-gray-300 bg-white shadow-lg">
                {DELIVERED_RX_REASON_SNOOZE_OPTIONS.filter((option) => option.value !== 'custom').map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSkipClick({ minutes: option.minutes })}
                    className="block w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {option.label}
                  </button>
                ))}
                <div className="border-t border-gray-200 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setSnoozeOption('custom')}
                    className={`${snoozeOption === 'custom' ? 'mb-2' : ''} block w-full text-left text-sm font-semibold text-gray-700`}
                  >
                    Custom
                  </button>
                  {snoozeOption === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={customSnoozeTime}
                        onChange={(event) => setCustomSnoozeTime(event.target.value)}
                        className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 px-2 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleSkipClick({ customTime: customSnoozeTime })}
                        disabled={isSkipDisabled}
                        className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
                          isSkipDisabled
                            ? 'cursor-not-allowed bg-slate-100 text-slate-500'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        Set
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className={`rounded-md px-5 py-2 text-sm font-semibold transition ${
              submitting
                ? 'cursor-not-allowed bg-blue-400 text-white'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {submitting ? 'Sending to WinRx...' : 'Send to WinRx now'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UnsentScheduleSendPromptManager = ({ pharmacyId, isPharmacist, userId }) => {
  const [records, setRecords] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const pendingLoadRequestRef = useRef(null);

  const snoozeStorageKey = getSnoozeStorageKey(pharmacyId, userId);
  const [snoozeUntilMs, setSnoozeUntilMs] = useState(() => getStoredSnoozeUntilMs(snoozeStorageKey));

  const isSnoozed = useCallback(() => {
    const storedSnoozeUntilMs = getStoredSnoozeUntilMs(snoozeStorageKey);
    setSnoozeUntilMs(storedSnoozeUntilMs);
    return Boolean(storedSnoozeUntilMs);
  }, [snoozeStorageKey]);

  const loadUnsentRecords = useCallback(async () => {
    if (!pharmacyId || !isPharmacist) {
      setRecords([]);
      return;
    }

    if (isSnoozed()) {
      setRecords([]);
      return;
    }

    if (pendingLoadRequestRef.current) {
      return;
    }

    const loadRequest = Axios.get(endPoints.rx_changing_unsent_schedules);
    pendingLoadRequestRef.current = loadRequest;

    try {
      const response = await loadRequest;
      const responseRows = Array.isArray(response?.data?.data) ? response.data.data : [];
      setRecords(!isSnoozed() ? responseRows : []);
    } catch (error) {
      // Endpoint error or offline: retry on next poll interval
    } finally {
      if (pendingLoadRequestRef.current === loadRequest) {
        pendingLoadRequestRef.current = null;
      }
    }
  }, [isPharmacist, isSnoozed, pharmacyId]);

  useEffect(() => {
    loadUnsentRecords();
    const timer = window.setInterval(loadUnsentRecords, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadUnsentRecords]);

  useEffect(() => {
    if (!pharmacyId || !snoozeUntilMs) {
      return undefined;
    }

    const delay = snoozeUntilMs - Date.now();
    if (delay <= 0) {
      localStorage.removeItem(snoozeStorageKey);
      setSnoozeUntilMs(null);
      loadUnsentRecords();
      return undefined;
    }

    const timer = setTimeout(() => {
      localStorage.removeItem(snoozeStorageKey);
      setSnoozeUntilMs(null);
      loadUnsentRecords();
    }, delay);

    return () => clearTimeout(timer);
  }, [loadUnsentRecords, pharmacyId, snoozeStorageKey, snoozeUntilMs]);

  const handleSubmit = async () => {
    if (!records.length) return;

    setSubmitting(true);
    try {
      const response = await Axios.post(endPoints.rx_changing_unsent_schedules_send, {});
      const message = response?.data?.message || 'Schedules sent to WinRx.';
      safeToast.success(message);
      setRecords([]);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Unable to send schedules to WinRx.';
      safeToast.error(message);
      // Snooze on failure for 30 minutes so bridge retry isn't spammed
      handleSkip({ minutes: 30 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = (snoozeConfig = {}) => {
    const snoozeUntilDate = getDeliveredRxReasonSnoozeUntilDate(snoozeConfig);
    const snoozeUntilTime = snoozeUntilDate?.getTime();

    if (!snoozeUntilDate || Number.isNaN(snoozeUntilTime) || snoozeUntilTime <= Date.now()) {
      return;
    }

    localStorage.setItem(snoozeStorageKey, JSON.stringify({
      snoozeUntil: snoozeUntilDate.toISOString(),
    }));
    setSnoozeUntilMs(snoozeUntilTime);
    setRecords([]);
  };

  return (
    <UnsentScheduleSendPromptModal
      records={records}
      totalCount={records.length}
      submitting={submitting}
      onSubmit={handleSubmit}
      onSkip={handleSkip}
    />
  );
};

export default UnsentScheduleSendPromptManager;

